// Settings-sida — hanterar konto-inställningar.
// Kräver inloggning (skyddad av ProtectedRoute).

import { useState, type FormEvent } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

type Section = 'account' | 'password' | 'appearance' | 'danger';

export default function Settings() {
  const { user, changePassword, updateEmail, deleteAccount, logout } = useAuth();
  const { theme, setTheme, isDark } = useTheme();
  const [section, setSection] = useState<Section>('account');

  // Change password
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);

  // Update email
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailSubmitting, setEmailSubmitting] = useState(false);

  // Delete account
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  if (!user) return null;

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);
    if (newPassword !== newPasswordConfirm) {
      setPasswordError('Lösenorden matchar inte');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('Lösenordet måste vara minst 8 tecken');
      return;
    }
    setPasswordSubmitting(true);
    try {
      await changePassword(oldPassword, newPassword);
      setPasswordSuccess(true);
      setOldPassword('');
      setNewPassword('');
      setNewPasswordConfirm('');
    } catch (err) {
      setPasswordError(
        err instanceof Error ? err.message : 'Kunde inte ändra lösenord',
      );
    } finally {
      setPasswordSubmitting(false);
    }
  };

  const handleEmailSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setEmailError(null);
    setEmailSuccess(false);
    if (!newEmail.includes('@')) {
      setEmailError('Ogiltig e-post');
      return;
    }
    setEmailSubmitting(true);
    try {
      await updateEmail(newEmail, emailPassword);
      setEmailSuccess(true);
      setNewEmail('');
      setEmailPassword('');
    } catch (err) {
      setEmailError(
        err instanceof Error ? err.message : 'Kunde inte uppdatera e-post',
      );
    } finally {
      setEmailSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirm !== user.email) {
      setDeleteError('Skriv din e-post exakt för att bekräfta');
      return;
    }
    setDeleteError(null);
    setDeleteSubmitting(true);
    try {
      await deleteAccount();
    } catch (err) {
      setDeleteError(
        err instanceof Error ? err.message : 'Kunde inte radera konto',
      );
      setDeleteSubmitting(false);
    }
  };

  const sectionBtn = (s: Section, label: string) => (
    <button
      key={s}
      type="button"
      onClick={() => setSection(s)}
      className={`block w-full rounded px-3 py-2 text-left text-sm transition ${
        section === s
          ? 'bg-indigo-50 font-medium text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300'
          : 'text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
      <h1 className="mb-6 text-2xl font-bold text-slate-900 dark:text-white">
        Inställningar
      </h1>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-[200px_1fr]">
        {/* Sidebar */}
        <nav className="space-y-1">
          {sectionBtn('account', 'Konto')}
          {sectionBtn('password', 'Lösenord')}
          {sectionBtn('appearance', 'Utseende')}
          {sectionBtn('danger', 'Radera konto')}
        </nav>

        {/* Content */}
        <div>
          {section === 'account' && (
            <div className="space-y-4 rounded border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                Kontoinformation
              </h2>
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="font-medium text-slate-500 dark:text-slate-400">E-post</dt>
                  <dd className="text-slate-900 dark:text-white">{user.email}</dd>
                </div>
                <div>
                  <dt className="font-medium text-slate-500 dark:text-slate-400">Verifierad</dt>
                  <dd className="text-slate-900 dark:text-white">
                    {user.verified ? 'Ja' : 'Nej'}
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-slate-500 dark:text-slate-400">Skapad</dt>
                  <dd className="text-slate-900 dark:text-white">
                    {new Date(user.created).toLocaleDateString('sv-SE')}
                  </dd>
                </div>
              </dl>

              <hr className="border-slate-200 dark:border-slate-800" />

              <h3 className="text-md font-semibold text-slate-900 dark:text-white">
                Uppdatera e-post
              </h3>
              {emailSuccess && (
                <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400">
                  E-post uppdaterad!
                </div>
              )}
              {emailError && (
                <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                  {emailError}
                </div>
              )}
              <form onSubmit={handleEmailSubmit} className="space-y-3">
                <input
                  type="email"
                  placeholder="Ny e-post"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  required
                  className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                />
                <input
                  type="password"
                  placeholder="Bekräfta med lösenord"
                  value={emailPassword}
                  onChange={(e) => setEmailPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                />
                <button
                  type="submit"
                  disabled={emailSubmitting}
                  className="rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {emailSubmitting ? 'Uppdaterar...' : 'Uppdatera e-post'}
                </button>
              </form>
            </div>
          )}

          {section === 'password' && (
            <div className="space-y-4 rounded border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                Ändra lösenord
              </h2>
              {passwordSuccess && (
                <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400">
                  Lösenord uppdaterat!
                </div>
              )}
              {passwordError && (
                <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                  {passwordError}
                </div>
              )}
              <form onSubmit={handlePasswordSubmit} className="space-y-3">
                <input
                  type="password"
                  placeholder="Nuvarande lösenord"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                />
                <input
                  type="password"
                  placeholder="Nytt lösenord (minst 8 tecken)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                />
                <input
                  type="password"
                  placeholder="Bekräfta nytt lösenord"
                  value={newPasswordConfirm}
                  onChange={(e) => setNewPasswordConfirm(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                />
                <button
                  type="submit"
                  disabled={passwordSubmitting}
                  className="rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {passwordSubmitting ? 'Ändrar...' : 'Ändra lösenord'}
                </button>
              </form>
            </div>
          )}

          {section === 'appearance' && (
            <div className="space-y-4 rounded border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                Utseende
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Välj hur Investen ska visas. &quot;Följ system&quot; använder din enhets
                inställning.
              </p>
              <div className="space-y-2">
                {(['light', 'dark', 'system'] as const).map((t) => (
                  <label
                    key={t}
                    className="flex cursor-pointer items-center gap-3 rounded border border-slate-200 p-3 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
                  >
                    <input
                      type="radio"
                      name="theme"
                      value={t}
                      checked={theme === t}
                      onChange={() => setTheme(t)}
                      className="accent-indigo-600"
                    />
                    <div>
                      <div className="font-medium text-slate-900 dark:text-white">
                        {t === 'light' && 'Ljust'}
                        {t === 'dark' && 'Mörkt'}
                        {t === 'system' && 'Följ system'}
                        {theme === t && isDark && t === 'system' && ' (för närvarande mörkt)'}
                        {theme === t && !isDark && t === 'system' && ' (för närvarande ljust)'}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {t === 'light' && 'Alltid ljust tema'}
                        {t === 'dark' && 'Alltid mörkt tema'}
                        {t === 'system' && 'Följer din enhets inställning'}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {section === 'danger' && (
            <div className="space-y-4 rounded border border-red-200 bg-white p-6 dark:border-red-800 dark:bg-slate-900">
              <h2 className="text-lg font-semibold text-red-600 dark:text-red-400">
                Radera konto
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Detta tar bort ditt konto permanent. Dina stödlinjer i charts
                kommer också att raderas.
              </p>
              {deleteError && (
                <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                  {deleteError}
                </div>
              )}
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder={`Skriv "${user.email}" för att bekräfta`}
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleteSubmitting || deleteConfirm !== user.email}
                    className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    {deleteSubmitting ? 'Raderar...' : 'Radera permanent'}
                  </button>
                  <button
                    type="button"
                    onClick={logout}
                    className="rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    Logga ut
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
